import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { User, LogOut } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useViewAs } from "@/contexts/ViewAsContext";
import { useAuth } from "@/contexts/AuthContext";
import { OutspokenWordmark } from "@/components/OutspokenLogo";

const navItems = [
  { label: "Sessions", href: "/sessions", description: "Bring a real situation, get clarity" },
  { label: "Journal", href: "/journal", description: "Decisions you've taken, and how they went" },
  { label: "Reflections", href: "/reflections", description: "Patterns and insights from your sessions" },
  { label: "Dojo", href: "/dojo", description: "Train your thinking on real scenarios" },
  { label: "Newsletter", href: "/newsletter", description: "One clear idea, every week" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const { isViewingAs } = useViewAs();

  // Frosted glass on scroll
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [location.pathname]);


  return (
    <nav className="fixed left-0 right-0 z-50" style={{ top: isViewingAs ? 36 : 0 }}>
      <div className={`relative transition-all duration-300 ${scrolled ? "nav-solid" : "bg-background/80 backdrop-blur-xl border-b border-[hsl(var(--iron)/0.4)]"}`}>
        <div className="container flex items-center justify-between h-16 sm:h-[4.5rem] px-4 sm:px-6">
          {/* Serif wordmark */}
          <Link
            to="/"
            state={{ fromNav: true }}
            onClick={() => {
              if (location.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="text-manuscript hover:text-marigold transition-colors flex items-center group"
          >
            <OutspokenWordmark height={26} className="group-hover:text-marigold transition-colors duration-300" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`relative px-4 py-2 text-[11px] font-medium tracking-[0.22em] uppercase transition-colors duration-300 font-sans ${
                  location.pathname === item.href
                    ? "text-marigold"
                    : "text-manuscript/70 hover:text-manuscript"
                }`}
              >
                {location.pathname === item.href && (
                  <motion.span
                    layoutId="nav-active"
                    className="absolute inset-x-3 -bottom-px h-px bg-marigold"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
                <span className="relative z-10">{item.label}</span>
              </Link>
            ))}


            <div className="w-px h-5 bg-manuscript/15 mx-3" />

            {user ? (
              <div className="flex items-center gap-2">
                <Link
                  to="/dashboard"
                  className="btn-ghost"
                  style={{ padding: "0.55rem 1.1rem", fontSize: "10px", letterSpacing: "0.2em", minHeight: 0 }}
                >
                  <User size={13} />
                  {profile?.display_name || "Dashboard"}
                </Link>
                <button
                  onClick={signOut}
                  className="text-manuscript/60 hover:text-manuscript transition-all duration-300 p-2.5 min-w-[40px] min-h-[40px] flex items-center justify-center"
                  aria-label="Sign out"
                >
                  <LogOut size={15} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link
                  to="/auth"
                  className="text-manuscript/70 hover:text-manuscript px-3 py-2 text-[11px] font-medium tracking-[0.22em] uppercase transition-all duration-300 font-sans"
                >
                  Sign In
                </Link>
                <Link to="/coach" className="btn-primary"
                  style={{ padding: "0.65rem 1.4rem", fontSize: "10.5px", minHeight: 0 }}
                >
                  Start a Session
                </Link>
              </div>
            )}
          </div>

          {/* Mobile: persistent CTA + hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <Link
              to="/coach"
              className="btn-primary"
              style={{ padding: "0.55rem 0.95rem", fontSize: "10px", minHeight: 0, letterSpacing: "0.18em" }}
            >
              Start a Session
            </Link>
            <button
              onClick={() => setOpen(!open)}
              className="relative w-11 h-11 flex items-center justify-center rounded-full transition-colors"
              aria-label={open ? "Close navigation menu" : "Open navigation menu"}
            >
              <div className="relative w-5 h-5">
                <motion.span
                  className="absolute left-0 w-full h-[2px] bg-foreground rounded-full"
                  style={{ top: "20%" }}
                  animate={open ? { rotate: 45, y: 6, width: "100%" } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }}
                />
                <motion.span
                  className="absolute left-0 w-3/4 h-[2px] bg-accent rounded-full"
                  style={{ top: "50%", translateY: "-50%" }}
                  animate={open ? { opacity: 0, x: 12 } : { opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                />
                <motion.span
                  className="absolute left-0 w-full h-[2px] bg-foreground rounded-full"
                  style={{ bottom: "20%" }}
                  animate={open ? { rotate: -45, y: -6, width: "100%" } : { rotate: 0, y: 0 }}
                  transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }}
                />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile overlay menu — sits below the navbar so the logo + close button stay visible */}
      <AnimatePresence>
        {open && (
          <>
            {/* Click-outside backdrop (closes menu) */}
            <motion.button
              type="button"
              aria-label="Close navigation menu"
              onClick={() => setOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden fixed inset-x-0 bottom-0 z-30 bg-background/40 backdrop-blur-sm"
              style={{ top: (isViewingAs ? 36 : 0) + 64 }}
            />

            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }}
              className="md:hidden fixed left-0 right-0 z-40 flex flex-col"
              style={{ top: (isViewingAs ? 36 : 0) + 64, maxHeight: `calc(100dvh - ${(isViewingAs ? 36 : 0) + 64}px)` }}
            >
              {/* Surface — matches site's ink + manuscript tokens */}
              <div className="relative bg-background/98 backdrop-blur-2xl border-b border-accent/15 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)]">
                {/* Subtle top accent line */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent" />

                <div className="px-6 pt-6 pb-8 overflow-y-auto" style={{ maxHeight: `calc(100dvh - ${(isViewingAs ? 36 : 0) + 64}px)` }}>
                  {/* Nav links */}
                  <nav className="flex flex-col gap-1">
                {navItems.map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -32 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -16 }}
                    transition={{ delay: 0.08 * i, duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
                  >
                    <Link
                      to={item.href}
                      onClick={() => setOpen(false)}
                      className={`group flex items-center justify-between py-3.5 border-b border-border/10 transition-colors ${
                        location.pathname === item.href
                          ? "text-accent"
                          : "text-foreground/85 hover:text-foreground"
                      }`}
                    >
                      <div>
                        <span className="font-serif-display text-xl leading-tight">{item.label}</span>
                        <p className="text-[10px] text-muted-foreground mt-1 font-medium tracking-[0.14em] uppercase">{item.description}</p>
                      </div>
                      <motion.span
                        className="text-accent/50 group-hover:text-accent text-base transition-colors"
                        whileHover={{ x: 3 }}
                      >
                        →
                      </motion.span>
                    </Link>
                  </motion.div>
                ))}

                  </nav>

                  {/* Bottom CTA area */}
                  <motion.div
                    className="flex flex-col gap-3 pt-6 mt-4 border-t border-border/10"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25, duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
                  >
                    {user ? (
                      <>
                        <Link
                          to="/dashboard"
                          onClick={() => setOpen(false)}
                          className="btn-ghost btn-ghost--marigold w-full"
                        >
                          <User size={15} />
                          {profile?.display_name || "Dashboard"}
                        </Link>
                        <button
                          onClick={() => { signOut(); setOpen(false); }}
                          className="btn-ghost w-full"
                        >
                          <LogOut size={14} />
                          Sign Out
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/coach"
                          onClick={() => setOpen(false)}
                          className="btn-ghost btn-ghost--marigold w-full"
                        >
                          Start a Session
                        </Link>
                        <Link
                          to="/auth"
                          onClick={() => setOpen(false)}
                          className="btn-ghost w-full"
                        >
                          Sign In
                        </Link>
                      </>
                    )}
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
}
